import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    GraduationCap,
    CreditCard,
    Code2,
    Share2,
    Layers,
    ArrowRight,
    SlidersHorizontal,
} from "lucide-react";
import { CountUp, SpotlightCard } from "@/components/animations";
import { EASING, DURATION, SPRING } from "@/lib/motionSystem";
import { tierMeta, getTier } from "@/lib/tiers";


function simulateScore(params) {
    let academic = (params.cgpa / 10) * 100;
    if (params.tier === "tier_1") academic = Math.min(100, academic + 10);
    if (params.tier === "tier_2") academic = Math.min(100, academic + 5);

    const feePatternMap = { lump_sum: 85, semester: 75, quarterly: 65, monthly: 55, irregular: 35 };
    const feeTimingMap = { always: 100, mostly: 75, sometimes: 50, often: 25 };
    const fee = (feePatternMap[params.feePattern] + feeTimingMap[params.feeTiming]) / 2;

    const dev = Math.min(100, params.repos * 3 + 20);
    const social = Math.min(100, Math.log10(params.followers + 1) * 25);
    const coverage = params.signals * 20;

    const raw = academic * 0.3 + fee * 0.25 + dev * 0.2 + social * 0.15 + coverage * 0.1;
    const score = Math.round(300 + (raw / 100) * 600);
    const tierLabel = getTier(score);

    return {
        score,
        tier: tierLabel,
        academic: Math.round(academic),
        fee: Math.round(fee),
        dev: Math.round(dev),
        social: Math.round(social),
        coverage: Math.round(coverage),
    };
}

function SignalBar({ label, value, icon: Icon, color }) {
    return (
        <div className="flex items-center gap-3">
            <Icon size={14} style={{ color }} />
            <span className="text-text-secondary text-xs w-20 shrink-0">{label}</span>
            <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                <motion.div
                    className="h-full rounded-full"
                    style={{
                        background:
                            value >= 70
                                ? "linear-gradient(90deg, #22c55e, #4ade80)"
                                : value >= 40
                                    ? "linear-gradient(90deg, #eab308, #facc15)"
                                    : "linear-gradient(90deg, #ef4444, #f97316)",
                    }}
                    initial={{ width: 0 }}
                    animate={{ width: `${value}%` }}
                    transition={{ duration: DURATION.sectionReveal, ease: EASING.smooth }}
                />
            </div>
            <span className="text-text-secondary text-xs w-8 text-right font-mono">{value}</span>
        </div>
    );
}

function RangeSlider({ label, value, onChange, min, max, step = 1, suffix = "" }) {
    return (
        <div>
            <div className="flex justify-between items-center mb-1.5">
                <span className="text-text-secondary text-xs">{label}</span>
                <span className="text-text-primary text-xs font-mono font-semibold">{value}{suffix}</span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(event) => onChange(Number(event.target.value))}
                className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-blue-primary [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-blue-primary [&::-webkit-slider-thumb]:shadow-[0_0_8px_rgba(99,102,241,0.7)]"
                data-testid={`sim-slider-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            />
        </div>
    );
}

function SelectInput({ label, value, onChange, options }) {
    return (
        <div>
            <span className="text-text-secondary text-xs block mb-1.5">{label}</span>
            <select
                value={value}
                onChange={(event) => onChange(event.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-blue-primary/40"
                data-testid={`sim-select-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            >
                {options.map(([val, text]) => <option key={val} value={val}>{text}</option>)}
            </select>
        </div>
    );
}

function RangeShiftVisual({ value }) {
    return (
        <div className="relative h-10 rounded-lg bg-white/[0.03] border border-white/10 overflow-hidden">
            <motion.div
                className="absolute inset-y-0 left-0 rounded-r-lg"
                style={{
                    background: "linear-gradient(90deg, rgba(99,102,241,0.4), rgba(6,182,212,0.42))",
                }}
                animate={{ width: `${Math.min(100, Math.max(0, ((value - 300) / 600) * 100))}%` }}
                transition={SPRING.soft}
            />
            <div className="absolute inset-0 flex items-center justify-between px-3 text-[11px] text-text-secondary">
                <span>Starter</span>
                <span>Builder</span>
                <span>Pioneer</span>
            </div>
        </div>
    );
}

export default function ScoreSimulator({ compact = false }) {
    const [cgpa, setCgpa] = useState(7.0);
    const [tier, setTier] = useState("tier_2");
    const [feePattern, setFeePattern] = useState("semester");
    const [feeTiming, setFeeTiming] = useState("mostly");
    const [repos, setRepos] = useState(5);
    const [followers, setFollowers] = useState(100);
    const [signals, setSignals] = useState(2);

    const result = useMemo(
        () => simulateScore({ cgpa, tier, feePattern, feeTiming, repos, followers, signals }),
        [cgpa, tier, feePattern, feeTiming, repos, followers, signals],
    );

    const tierStyle = tierMeta(result.tier);

    return (
        <SpotlightCard className={compact ? "p-4" : "p-6 md:p-8"} spotlightColor="rgba(99,102,241,0.16)">
            <div className="flex items-center gap-2 mb-1">
                <SlidersHorizontal size={18} className="text-blue-primary" />
                <h3 className={`font-heading font-bold tracking-tight text-text-primary ${compact ? "text-lg" : "text-xl sm:text-2xl"}`}>Score Simulator</h3>
            </div>
            <p className="text-text-secondary text-xs mb-5">Estimated preview. Spring-based transitions mirror real score shifts.</p>

            <div className={compact ? "" : "grid md:grid-cols-2 gap-8"}>
                <div className="space-y-4" data-testid="sim-controls">
                    <RangeSlider label="CGPA" value={cgpa} onChange={setCgpa} min={0} max={10} step={0.1} />
                    <SelectInput
                        label="Institution Tier"
                        value={tier}
                        onChange={setTier}
                        options={[
                            ["tier_1", "Tier 1 (IIT/NIT/BITS)"],
                            ["tier_2", "Tier 2 (VIT/SRM/Manipal)"],
                            ["tier_3", "Tier 3 (Other)"],
                        ]}
                    />
                    <SelectInput
                        label="Fee Payment Pattern"
                        value={feePattern}
                        onChange={setFeePattern}
                        options={[
                            ["lump_sum", "Lump Sum"],
                            ["semester", "Semester"],
                            ["quarterly", "Quarterly"],
                            ["monthly", "Monthly"],
                            ["irregular", "Irregular"],
                        ]}
                    />
                    <SelectInput
                        label="Payment Timing"
                        value={feeTiming}
                        onChange={setFeeTiming}
                        options={[
                            ["always", "Always On Time"],
                            ["mostly", "Mostly On Time"],
                            ["sometimes", "Sometimes Late"],
                            ["often", "Often Late"],
                        ]}
                    />
                    <RangeSlider label="GitHub Repos" value={repos} onChange={setRepos} min={0} max={50} />
                    <RangeSlider label="Social Followers" value={followers} onChange={setFollowers} min={0} max={10000} step={50} />
                    <RangeSlider label="Signals Connected" value={signals} onChange={setSignals} min={1} max={5} suffix="/5" />
                </div>

                <div className={compact ? "mt-5" : ""}>
                    <div className={`text-center ${compact ? "mb-4" : "mb-6"}`}>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={`${result.score}-${result.tier}`}
                                initial={{ opacity: 0, y: 8, scale: 0.96 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -8, scale: 0.96 }}
                                transition={SPRING.score}
                                className="inline-block"
                            >
                                <CountUp
                                    target={result.score}
                                    duration={1.25}
                                    className={`font-mono font-bold tracking-tight ${compact ? "text-5xl" : "text-6xl sm:text-7xl"} text-text-primary`}
                                    data-testid="sim-score-value"
                                />
                                <span className="text-text-secondary/50 text-lg ml-1">/ 900</span>
                            </motion.div>
                        </AnimatePresence>
                        <div className="mt-2">
                            <motion.span
                                className={`inline-block px-4 py-1 rounded-full text-sm font-semibold ${tierStyle.bg} ${tierStyle.text} ${tierStyle.shadow}`}
                                layout
                                transition={SPRING.soft}
                                data-testid="sim-tier-badge"
                            >
                                {result.tier}
                            </motion.span>
                        </div>
                    </div>

                    <RangeShiftVisual value={result.score} />

                    <div className="space-y-2.5 mt-4" data-testid="sim-signal-bars">
                        <SignalBar label="Academic" value={result.academic} icon={GraduationCap} color="#6366f1" />
                        <SignalBar label="Fee" value={result.fee} icon={CreditCard} color="#06b6d4" />
                        <SignalBar label="Developer" value={result.dev} icon={Code2} color="#a855f7" />
                        <SignalBar label="Social" value={result.social} icon={Share2} color="#eab308" />
                        <SignalBar label="Coverage" value={result.coverage} icon={Layers} color="#22c55e" />
                    </div>

                    {!compact && (
                        <div className="mt-6 text-center">
                            <Link
                                to="/genz-credit-scoring/flow"
                                className="bg-blue-primary text-white px-6 py-2.5 rounded-xl font-medium text-sm hover:bg-blue-primary/90 transition-all inline-flex items-center gap-2"
                                data-testid="sim-cta-btn"
                            >
                                Get Your Real Score <ArrowRight size={16} />
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </SpotlightCard>
    );
}
